import {TakipAcStatusEnum} from "@/plugins/uyap-plugin/takip_ac/parser/TakipAcStatusEnum";
import {TaskDurum} from "@/plugins/uyap-plugin/enum/TaskDurum";
import {uyapEngelDurdurmaliMi} from "@/plugins/uyap-plugin/UyapEngeli";

/**
 * UYAP engelinde panel döngülerinin ortak yardımcıları (DEV-3572): XML ile takip hazırlama (P2), Hemen Aç (P3),
 * Toplu Haciz / Genel Talep / Tebligat "Hazırla" (P4). Saf modül: `@/main`, store ve DOM import ETMEZ.
 */

export const ENGEL_SORGULANMADI_MESAJI = "UYAP engeli nedeniyle sorgulanmadı";
export const ENGEL_HAZIRLANMADI_MESAJI = "UYAP engeli nedeniyle hazırlanmadı";
/** Beklemedeki (sorgulanmamış) dosyanın durum metni (SOZLESME §8.2). */
export const ENGEL_BEKLEMEDE_METNI = "Beklemede: " + ENGEL_SORGULANMADI_MESAJI;

/** 1240 -> '1.240' (toLocaleString KULLANILMAZ: ICU'ya bağlı). */
export function binlik(sayi: number): string {
    return String(sayi).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** K8: "N/M dosya sorgulandı, K dosya UYAP engeli nedeniyle sorgulanmadı." (N = M - K) */
export function xmlSorguEngelMesaji(toplam: number, bekleyen: number): string {
    return binlik(toplam - bekleyen) + "/" + binlik(toplam) + " dosya sorgulandı, " + binlik(bekleyen) + " dosya UYAP engeli nedeniyle sorgulanmadı.";
}

/** K8: "Hemen Aç durduruldu: UYAP engeli. X dosya işlendi, Y dosya sırada." */
export function hemenAcDurdurulduMesaji(islenen: number, sirada: number): string {
    return "Hemen Aç durduruldu: UYAP engeli. " + islenen + " dosya işlendi, " + sirada + " dosya sırada.";
}

/**
 * Döngüyü kesme kararı: kilit sürüyor ya da hata engel/kapı hatası (plugin `uyapEngelDurdurmaliMi`). Yürütücüler
 * hatayı `new Error(e.message)` ile kodsuz sarabildiği için hata koduna tek başına güvenilmez; kilit durumu da okunur.
 */
export function engelNedeniyleKes(hata?: any): boolean {
    return uyapEngelDurdurmaliMi(hata);
}

export interface SorguSatiri {
    status: any;
    tempId: number;
    parsedFile: any;
    messages?: any;
    addMessage(tip: any, mesaj: any): void;
}

/** Sorgulanacak satırlar: yalnız QUERY_PENDING ve XML'i çözülmüş (parsedFile dolu) olanlar. */
export function bekleyenleriSec<T extends SorguSatiri>(dosyalar: T[]): T[] {
    return dosyalar.filter((d) => d.status === TakipAcStatusEnum.QUERY_PENDING && !!d.parsedFile);
}

export function bekleyenSayisi<T extends SorguSatiri>(dosyalar: T[]): number {
    return dosyalar.filter((d) => d.status === TakipAcStatusEnum.QUERY_PENDING).length;
}

/**
 * UYAP engelinde sorgulanmayan dosyanın durum metni (SOZLESME §8.2): son mesaj engel mesajıysa
 * "Beklemede: UYAP engeli nedeniyle sorgulanmadı"; aksi halde null (bileşenin mevcut durum metni geçerli).
 */
export function engelBeklemeDurumMetni(dosya: { status: any, messages?: any }): string | null {
    if (!dosya || dosya.status !== TakipAcStatusEnum.QUERY_PENDING) return null;
    const mesajlar = dosya.messages;
    const son = Array.isArray(mesajlar) && mesajlar.length > 0 ? mesajlar[mesajlar.length - 1] : null;
    return son && son.message === ENGEL_SORGULANMADI_MESAJI ? ENGEL_BEKLEMEDE_METNI : null;
}

function pendingMesajiYaz(satir: SorguSatiri): void {
    const mesajlar = satir.messages;
    const son = Array.isArray(mesajlar) && mesajlar.length > 0 ? mesajlar[mesajlar.length - 1] : null;
    if (!son || son.message !== ENGEL_SORGULANMADI_MESAJI) satir.addMessage("info", ENGEL_SORGULANMADI_MESAJI);
}

export interface SorguDongusuGirdisi<T extends SorguSatiri> {
    /** Sorgulanacak satırlar (QUERY_PENDING, parsedFile dolu), sıralı. */
    kuyruk: T[];
    engelVarMi: () => boolean;
    /** runQuery + checkDuplicateTakip; hatayı kendi içinde QUERY_FAIL'e çevirir (mevcut davranış), FIRLATMAZ. */
    dosyayiSorgula: (satir: T) => Promise<void>;
    /** Uçuştaki (yarım kalmış, kirli) dosyanın XML'den yeniden kurulmuş QUERY_PENDING kopyası. */
    taze: (satir: T) => Promise<T>;
    ilerle: (yapilan: number, toplam: number) => Promise<void> | void;
}

export interface SorguDongusuSonucu<T extends SorguSatiri> {
    engeleTakildi: boolean;
    /** tempId -> yerine konacak satır (yalnız uçuştaki dosya). */
    yerine: Map<number, T>;
    sorgulanan: number;
}

/**
 * XML sihirbazlarının sorgu döngüsü (P2). Her dosyadan ÖNCE kilit kontrol edilir: kilitliyse dosyaya
 * girilmez (UYAP'a istek yok). Her dosyadan SONRA da kontrol edilir: plugin taraf/mükerrer hatalarını
 * (eski sürümlerde) yutabildiği için engel dosyanın ortasında gelince o dosya 'sorgulandı' görünür ama borçlusu
 * 'TC hatalı' işaretlenmiş olabilir; böyle dosya `taze()` ile baştan kurulup bekleyen sayılır. Kalanlar
 * QUERY_PENDING kalır ve satırlarına mesaj yazılır.
 */
export async function xmlSorguDongusu<T extends SorguSatiri>(girdi: SorguDongusuGirdisi<T>): Promise<SorguDongusuSonucu<T>> {
    const yerine = new Map<number, T>();
    let sorgulanan = 0;
    for (let k = 0; k < girdi.kuyruk.length; k++) {
        const satir = girdi.kuyruk[k];
        if (girdi.engelVarMi()) {
            for (let j = k; j < girdi.kuyruk.length; j++) pendingMesajiYaz(girdi.kuyruk[j]);
            return {engeleTakildi: true, yerine: yerine, sorgulanan: sorgulanan};
        }
        await girdi.dosyayiSorgula(satir);
        if (girdi.engelVarMi()) {
            const taze = await girdi.taze(satir);
            yerine.set(satir.tempId, taze);
            pendingMesajiYaz(taze);
            for (let j = k + 1; j < girdi.kuyruk.length; j++) pendingMesajiYaz(girdi.kuyruk[j]);
            return {engeleTakildi: true, yerine: yerine, sorgulanan: sorgulanan};
        }
        sorgulanan++;
        await girdi.ilerle(sorgulanan, girdi.kuyruk.length);
    }
    return {engeleTakildi: false, yerine: yerine, sorgulanan: sorgulanan};
}

function secilebilirMi(d: SorguSatiri): boolean {
    return d.status !== TakipAcStatusEnum.QUERY_FAIL
        && d.status !== TakipAcStatusEnum.DUPLICATE_WARNING
        && d.status !== TakipAcStatusEnum.QUERY_PENDING;
}

/**
 * Sorgu sonrası seçim. İlk tur: `yeniTempIdler = null` (hepsi yeni): seçilebilir olanlar seçilir (mevcut
 * kural + PENDING dışlanır). "Kalanı sorgula": önceki seçim KORUNUR, yeni sorgulananlardan seçilebilir
 * olanlar eklenir.
 */
export function sorguSonrasiSecim<T extends SorguSatiri>(tumu: T[], oncekiSecimTempIdler: number[], yeniTempIdler: number[] | null): T[] {
    const onceki = new Set<number>(oncekiSecimTempIdler);
    const yeni = yeniTempIdler === null ? null : new Set<number>(yeniTempIdler);
    return tumu.filter((d) => onceki.has(d.tempId) || ((yeni === null || yeni.has(d.tempId)) && secilebilirMi(d)));
}

/**
 * P4 (Toplu Haciz / Genel Talep / Tebligat Hazırla): `baslangic` ve sonrasındaki hazırlanmamış görevleri
 * "UYAP engeli nedeniyle hazırlanmadı" olarak işaretler. Adım-3 tabloları yalnız BITTI_BASARILI ve
 * BITTI_HATALI satırlarını çizdiği için görev BITTI_HATALI olur; `engelNedeniyleHazirlanmadi` bayrağı
 * yeniden "Hazırla"da SIRADA'ya döndürmek içindir. `nesneAciklama`: Genel Talep/Tebligat şablonları
 * `description.message` okuyor, Haciz ise düz metin.
 *
 * `ucusta` (varsayılan true): `baslangic` satırı UYAP isteğinin ortasında engele takıldıysa (iç/dış catch
 * yolları) satırın sonucu eksiktir; BITTI_BASARILI olsa bile işaretlenir. Döngü BAŞINDA kesildiğinde ise
 * hiçbir istek atılmamıştır (`ucusta=false`): `baslangic` satırı önceki turdan BITTI_BASARILI ise o da KORUNUR
 * (yoksa yeniden "Hazırla"da yalnız ilk satır hatalıya döner, hazır satır kaybolur). Her iki durumda
 * sonraki satırlarda BITTI_BASARILI olanlar korunur.
 */
export function engeleTakilanGorevleriIsaretle(takipler: any[], baslangic: number, nesneAciklama: boolean, ucusta: boolean = true): void {
    for (let i = baslangic; i < takipler.length; i++) {
        const task = takipler[i] ? takipler[i].task : null;
        if (!task) continue;
        if ((i > baslangic || !ucusta) && task.status === TaskDurum.BITTI_BASARILI) continue;
        task.status = TaskDurum.BITTI_HATALI;
        task.description = nesneAciklama ? {message: ENGEL_HAZIRLANMADI_MESAJI} : ENGEL_HAZIRLANMADI_MESAJI;
        task.engelNedeniyleHazirlanmadi = true;
    }
}

/** Yeniden "Hazırla": engel bayraklı görev tekrar SIRADA olur (kilit hâlâ sürüyorsa döngü başı onu yine işaretler). */
export function engelIsaretliGoreviSifirla(task: any): void {
    if (task && task.engelNedeniyleHazirlanmadi === true) {
        task.status = TaskDurum.SIRADA;
        task.description = undefined;
        task.engelNedeniyleHazirlanmadi = false;
    }
}
